import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import React, { useContext } from 'react';
import {
  Currency,
  LengthUnit,
  lengthUnitToString,
  WeightUnit,
  weightUnitToString,
} from '../../../../../types/product_master_data/productMasterData';
import { translate } from '../../../../../types/translation/Translator';
import { TextInput } from '../../../../components/TextInput';
import { DropdownSearchSelect } from '../../../../components/Dropdown';
import { CompanyRoleAssignmentContext } from '../../../../../context/CompanyRoleAssignmentContext';
import { ProductMasterDataInput } from '../../../../../types/product_master_data';

interface ProductMasterDataAttributePaneInputProps {
  input: ProductMasterDataInput;
  setInput: (pmd: ProductMasterDataInput) => void;
}

export default function ProductMasterDataAttributePane({ input, setInput }: ProductMasterDataAttributePaneInputProps) {
  const { isAdmin } = useContext(CompanyRoleAssignmentContext);

  return (
    <div className='grid grid-cols-3 gap-8'>
      {/* Column 1 */}
      <div className='grid gap-2'>
        <p className='section-title'>{translate({ key: TranslationKey.dimensions })}</p>

        <TextInput
          type={'number'}
          disabled={input.externalIdentifier != undefined || !isAdmin}
          value={input.dimensions?.height}
          label={translate({ key: TranslationKey.height })}
          placeholder={translate({ key: TranslationKey.height })}
          onChange={v => setInput(input.withHeight(v))}
        />

        <TextInput
          type={'number'}
          disabled={input.externalIdentifier != undefined || !isAdmin}
          value={input.dimensions?.width}
          label={translate({ key: TranslationKey.width })}
          placeholder={translate({ key: TranslationKey.width })}
          onChange={v => setInput(input.withWidth(v))}
        />

        <TextInput
          type={'number'}
          disabled={input.externalIdentifier != undefined || !isAdmin}
          value={input.dimensions?.depth}
          label={translate({ key: TranslationKey.depth })}
          placeholder={translate({ key: TranslationKey.depth })}
          onChange={v => setInput(input.withDepth(v))}
        />

        <DropdownSearchSelect
          disabled={input.externalIdentifier != undefined || !isAdmin}
          label={translate({ key: TranslationKey.unit })}
          placeholder={translate({ key: TranslationKey.unit })}
          items={[...Object.keys(LengthUnit)] as LengthUnit[]}
          value={input.dimensions?.unit}
          textBuilder={item => lengthUnitToString(item)}
          onSelect={item => setInput(input.withLengthUnit(item))}
        />
      </div>

      {/* Column 2 */}
      <div className='grid gap-2'>
        <p className='section-title'>{translate({ key: TranslationKey.mass })}</p>

        <TextInput
          type={'number'}
          disabled={input.externalIdentifier != undefined || !isAdmin}
          value={input.netWeight?.value}
          label={translate({ key: TranslationKey.netWeight })}
          placeholder={translate({ key: TranslationKey.netWeight })}
          onChange={v => setInput(input.withNetWeightValue(v))}
        />

        <DropdownSearchSelect
          disabled={input.externalIdentifier != undefined || !isAdmin}
          label={translate({ key: TranslationKey.unit })}
          placeholder={translate({ key: TranslationKey.unit })}
          items={[...Object.keys(WeightUnit)] as WeightUnit[]}
          value={input.netWeight?.unit}
          textBuilder={item => weightUnitToString(item)}
          onSelect={item => setInput(input.withNetWeightUnit(item))}
        />

        <TextInput
          type={'number'}
          disabled={input.externalIdentifier != undefined || !isAdmin}
          value={input.grossWeight?.value}
          label={translate({ key: TranslationKey.grossWeight })}
          placeholder={translate({ key: TranslationKey.grossWeight })}
          onChange={v => setInput(input.withGrossWeightValue(v))}
        />

        <DropdownSearchSelect
          disabled={input.externalIdentifier != undefined || !isAdmin}
          label={translate({ key: TranslationKey.unit })}
          placeholder={translate({ key: TranslationKey.unit })}
          items={[...Object.keys(WeightUnit)] as WeightUnit[]}
          value={input.grossWeight?.unit}
          textBuilder={item => weightUnitToString(item)}
          onSelect={item => setInput(input.withGrossWeightUnit(item))}
        />
      </div>

      {/* Column 3 */}
      <div className='grid gap-2'>
        <p className='section-title'>{translate({ key: TranslationKey.price })}</p>

        <TextInput
          type={'number'}
          disabled={input.externalIdentifier != undefined || !isAdmin}
          value={input.purchasePrice?.value}
          label={translate({ key: TranslationKey.purchasePrice })}
          placeholder={translate({ key: TranslationKey.purchasePrice })}
          onChange={v => setInput(input.withPurchasePriceValue(v))}
        />

        <DropdownSearchSelect
          disabled={input.externalIdentifier != undefined || !isAdmin}
          label={translate({ key: TranslationKey.currency })}
          placeholder={translate({ key: TranslationKey.currency })}
          items={[...Object.keys(Currency)] as Currency[]}
          value={input.purchasePrice?.currency}
          textBuilder={item => String(item).charAt(0).toUpperCase() + String(item).slice(1)}
          onSelect={item => setInput(input.withPurchasePriceCurrency(item))}
        />

        <TextInput
          type={'number'}
          disabled={input.externalIdentifier != undefined || !isAdmin}
          value={input.sellingPrice?.value}
          label={translate({ key: TranslationKey.sellingPrice })}
          placeholder={translate({ key: TranslationKey.sellingPrice })}
          onChange={v => setInput(input.withSellingPriceValue(v))}
        />

        <DropdownSearchSelect
          disabled={input.externalIdentifier != undefined || !isAdmin}
          label={translate({ key: TranslationKey.currency })}
          placeholder={translate({ key: TranslationKey.currency })}
          items={[...Object.keys(Currency)] as Currency[]}
          value={input.sellingPrice?.currency}
          textBuilder={item => String(item).charAt(0).toUpperCase() + String(item).slice(1)}
          onSelect={item => setInput(input.withSellingPriceCurrency(item))}
        />
      </div>
    </div>
  );
}
